import { apiFetch, apiUploadFormData } from "@/lib/api";
import type { LaborOperatorDisplay } from "@/lib/labor/laborOperatorPresence";

import type { InboundArrivalUnitKind } from "./inboundArrivalUnitKind";

export type InboundSourceType = "purchase_order" | "transfer_order";
export type InboundStatus = "draft" | "submitted" | "posted_to_mv";
export type CountMethod = "manual" | "scale_calc";
export type IqcStatus = "pending" | "passed" | "failed" | "skipped";

export interface InboundListRow {
  source_type: InboundSourceType;
  source_id: string;
  /** All purchase order ids when this card combines a multi-PO arrival. */
  source_ids?: string[];
  /** Display numbers for each purchase order on a combined multi-PO card. */
  source_numbers?: string[];
  source_number: string | null;
  source_status: string | null;
  supplier_or_origin: string | null;
  /** Comma-separated YYYY-MM-DD from MRP inbound (delivery splits and incoming fallback). */
  expected_dates: string | null;
  /** Earliest expected date for sorting; ISO date string. */
  expected_sort_date: string | null;
  /** Ordered quantity total for progress (same units as lines). */
  expected_quantity: string;
  /**
   * Same basis as sum of line `already_received_quantity`: Megaventory-received plus submitted
   * unposted hub receipts on purchase orders, transfer-order line received quantity on transfer orders.
   */
  system_received_quantity: string;
  /** Submitted hub receipt quantity that has not been posted back to Megaventory yet. */
  submitted_unposted_quantity: string;
  /** Sum of quantities on inbound hub receipts (draft and submitted) for this source. */
  locally_received_quantity: string;
  line_count: string;
  draft_receipt_id: string | null;
  latest_receipt_status: InboundStatus | null;
  latest_receipt_at: string | null;
  search_text: string | null;
  arrival_containers?: InboundQueueArrivalContainer[];
}

/** Dock unit linked to this order on the receive queue. */
export interface InboundQueueArrivalContainer {
  package_id: string;
  package_no: number;
  unit_kind: InboundArrivalUnitKind;
  arrival_id: string;
  scan_token: string;
  scan_label: string;
  arrived_at: string;
  received_at: string | null;
  package_received_at?: string | null;
  draft_receipt_id: string | null;
  current_location?: "holding" | "to_stock" | "shelf" | null;
}

export interface InboundSourceDetail {
  source_type: InboundSourceType;
  source_id: string;
  /** All purchase order ids when receiving a multi-PO arrival crate. */
  source_ids?: string[];
  /** Display numbers for each purchase order when receiving a multi-PO arrival crate. */
  source_numbers?: string[];
  source_number: string | null;
  source_status: string | null;
  supplier_or_origin: string | null;
  expected_dates: string | null;
  expected_sort_date: string | null;
}

export interface InboundLineRow {
  source_item_type: "purchase_order_item" | "transfer_order_item" | "ad_hoc";
  source_item_id: string;
  line_kind?: "order_line" | "unlisted_sku" | "non_sku";
  line_number: number | null;
  sku: string;
  /** Purchase-order SKU when receive identity is a finished good on a VALUE-ADD line. */
  po_sku?: string | null;
  /** Megaventory unit (`public.sku.mv_unit`), such as Pound(s). */
  unit_of_measure?: string | null;
  description: string | null;
  expected_quantity: string;
  /** Purchase order lines: comma-separated YYYY-MM-DD from MRP. */
  mrp_expected_dates: string | null;
  already_received_quantity: string;
  receipt_item_id: string | null;
  draft_received_quantity: string | null;
  count_method: CountMethod | null;
  condition_notes: string | null;
  iqc_status: IqcStatus | null;
  iqc_notes: string | null;
  iqc_passed_quantity: string | null;
  iqc_failed_quantity: string | null;
  iqc_skipped_quantity: string | null;
  iqc_at: string | null;
  /** True when this CPN has at least one approved AML manufacturer part number. */
  requires_mpn_entry: boolean;
  approved_component_id: string | null;
  scanned_mpn: string | null;
  unknown_mpn_confirmed: boolean;
  unknown_mpn_quantity: string | null;
  /** Flagged for a (later, async) second count. Null when there is no draft item yet. */
  requires_second_count: boolean | null;
  second_count_required_reason: string | null;
}

export interface InboundReceiptItemFlag {
  id: string;
  requires_second_count: boolean;
  second_count_required_reason: string | null;
}

export type SecondCountTaskStatus = "open" | "verified" | "mismatch" | "cancelled";

export interface InboundSecondCountTask {
  id: string;
  status: SecondCountTaskStatus;
  reason: string;
  inbound_receipt_item_id: string;
  sku: string | null;
  source_type: string;
  source_id: string;
  source_number: string | null;
  first_count_quantity: string;
  first_count_by: string | null;
  recount_quantity: string | null;
  recount_by: string | null;
  recount_at: string | null;
  created_at: string;
}

export interface InboundPackingSlip {
  id: string;
  filename: string;
  uploaded_at: string;
}

export interface InboundReceiptArrivalPackage {
  package_id: string;
  package_no: number;
  unit_kind: string;
}

export interface InboundReceiptSubcontainerItem {
  source_item_id: string;
  sku: string;
  quantity: string;
}

export interface InboundReceiptSubcontainer {
  id: string;
  subcontainer_no: number;
  unit_kind: string;
  status: string;
  closed_at: string;
  items: InboundReceiptSubcontainerItem[];
}

export interface InboundReceipt {
  id: string;
  source_type: InboundSourceType;
  source_id: string;
  location_id: number;
  inbound_arrival_id: string | null;
  inbound_arrival_package_id: string | null;
  arrival_packages: InboundReceiptArrivalPackage[];
  subcontainers: InboundReceiptSubcontainer[];
  packing_slip_storage_bucket: string | null;
  packing_slip_storage_path: string | null;
  packing_slip_filename: string | null;
  packing_slip_uploaded_at: string | null;
  packing_slips: InboundPackingSlip[];
  carrier: string | null;
  notes: string | null;
  open_subcontainer_unit_kind: string | null;
  status: InboundStatus;
  received_at: string | null;
  created_at: string;
  updated_at: string;
}

/** A pending/packing transfer order waiting on the PO being received (Layer 4). */
export interface InboundLinkedTransfer {
  id: string;
  number: string;
  status: string;
}

export interface InboundDetailResponse {
  source: InboundSourceDetail;
  lines: InboundLineRow[];
  receipts: InboundReceipt[];
  linked_transfers?: InboundLinkedTransfer[];
}

export interface InboundReceiptItemInput {
  source_item_id: number;
  received_quantity: number;
  count_method: CountMethod;
  condition_notes: string | null;
  iqc_status: IqcStatus;
  iqc_notes: string | null;
  iqc_passed_quantity: number | null;
  iqc_failed_quantity: number | null;
  iqc_skipped_quantity: number | null;
  approved_component_id?: number | null;
  scanned_mpn?: string | null;
  unknown_mpn_confirmed?: boolean;
  unknown_mpn_quantity?: number;
}

export interface InboundReceiptInput {
  source_type: InboundSourceType;
  source_id: number;
  inbound_arrival_package_id?: number | string | null;
  /** Optional; omitted or null clears any stored carrier on the draft. */
  carrier?: string | null;
  notes: string | null;
  items: InboundReceiptItemInput[];
  /** When true, keep the draft row even with zero counts and no packing slips (packing-slip upload prelude). */
  retain_empty_draft?: boolean;
}

/** Save a draft receipt, retaining an empty shell when needed for packing-slip upload. */
export async function ensureInboundDraftReceipt(input: InboundReceiptInput): Promise<InboundReceipt> {
  const saved = await saveInboundReceipt(input);
  if (saved != null) return saved;
  const retained = await saveInboundReceipt({ ...input, retain_empty_draft: true });
  if (retained == null) throw new Error("Could not save the draft receipt.");
  return retained;
}

export async function fetchInboundOrders(): Promise<InboundListRow[]> {
  const data = await apiFetch<{ rows: InboundListRow[] }>("/api/shipping/inbound");
  return data.rows;
}

export type ReceiveArrivalScanResult =
  | {
      kind: "arrival_package";
      pkg: {
        package_id: string;
        package_no: number;
        unit_kind: InboundArrivalUnitKind;
        arrival_id: string;
        vendor_name: string;
        po_number: string | null;
        carrier: string | null;
        tracking: string | null;
        receive_route: import("./inboundReceiveRoute").InboundReceiveRoute | null;
      };
      resolved_source: {
        source_type: InboundSourceType;
        source_id: string;
        source_number: string | null;
      } | null;
      resolved_sources: {
        source_type: InboundSourceType;
        source_id: string;
        source_number: string | null;
      }[];
    }
  | { kind: "sku"; sku: string }
  | { kind: "unknown"; scan: string };

export async function lookupReceiveArrivalScan(scan: string): Promise<ReceiveArrivalScanResult> {
  return apiFetch<ReceiveArrivalScanResult>("/api/shipping/inbound/receive/scan", {
    query: { scan: scan.trim() },
  });
}

export type ReceivePurchaseOrderQuickSearchRow = {
  id: number;
  number: string | null;
  status: string | null;
  supplier_name: string | null;
  supplier_id?: number | null;
};

export async function fetchReceivePurchaseOrderQuickSearch(
  query: string,
): Promise<ReceivePurchaseOrderQuickSearchRow[]> {
  const data = await apiFetch<{ rows: ReceivePurchaseOrderQuickSearchRow[] }>(
    "/api/shipping/inbound/receive/purchase-orders/quick-search",
    { query: { q: query.trim() } },
  );
  return data.rows;
}

export type ReceiveTransferOrderQuickSearchRow = {
  id: number;
  number: string | null;
  status: string | null;
};

export async function fetchReceiveTransferOrderQuickSearch(
  query: string,
): Promise<ReceiveTransferOrderQuickSearchRow[]> {
  const data = await apiFetch<{ rows: ReceiveTransferOrderQuickSearchRow[] }>(
    "/api/shipping/inbound/receive/transfer-orders/quick-search",
    { query: { q: query.trim() } },
  );
  return data.rows;
}

export async function applyInboundReceiveArrivalRoute(input: {
  scan: string;
  receive_route: import("./inboundReceiveRoute").InboundReceiveRoute;
  purchaseOrderId?: number;
  transferOrderId?: number;
  rmaId?: number;
  rmaNumber?: string;
  deviceSerials?: string[];
  packageId?: string;
}): Promise<{
  receive_route: import("./inboundReceiveRoute").InboundReceiveRoute;
  package_id: string;
  package_no: number;
  arrival_id: string;
  arrival_label_line: string | null;
  rma_number: string | null;
  resolved_source: {
    source_type: InboundSourceType;
    source_id: string;
    source_number: string | null;
  } | null;
}> {
  return apiFetch("/api/shipping/inbound/receive/arrival-packages/apply-receive-route", {
    method: "POST",
    body: {
      scan: input.scan.trim(),
      receive_route: input.receive_route,
      purchase_order_id: input.purchaseOrderId ?? null,
      transfer_order_id: input.transferOrderId ?? null,
      rma_id: input.rmaId ?? null,
      rma_number: input.rmaNumber ?? null,
      device_serials: input.deviceSerials ?? [],
      package_id: input.packageId ?? null,
    },
  });
}

export type InboundReceiveRmaDeviceMatch = {
  rmaId: string;
  rmaNumber: string;
  expectedCount: number;
  matchedSerials: string[];
  missingSerials: string[];
  countChecksOut: boolean;
};

export type InboundReceiveRmaDeviceCheckIn = {
  id: string;
  deviceSerial: string;
  sku: string | null;
  endUserName: string | null;
  checkedInAt: string;
};

export type InboundReceiveRmaDeviceCheckIns = {
  rmaId: string;
  rmaNumber: string;
  deviceSerials: string[];
  devices: InboundReceiveRmaDeviceCheckIn[];
};

export async function fetchInboundReceiveRmaDeviceCheckIns(
  rmaNumber: string,
): Promise<InboundReceiveRmaDeviceCheckIns> {
  return apiFetch("/api/shipping/inbound/receive/rma-device-check-ins", {
    query: { rma_number: rmaNumber.trim() },
  });
}

export async function saveInboundReceiveRmaDeviceCheckIn(input: {
  rmaNumber: string;
  deviceSerial: string;
  packageId?: string | null;
}): Promise<InboundReceiveRmaDeviceCheckIns> {
  return apiFetch("/api/shipping/inbound/receive/rma-device-check-ins", {
    method: "POST",
    body: {
      rma_number: input.rmaNumber.trim(),
      device_serial: input.deviceSerial.trim(),
      package_id: input.packageId ?? null,
    },
  });
}

export async function deleteInboundReceiveRmaDeviceCheckIn(input: {
  rmaNumber: string;
  checkInId: string;
}): Promise<InboundReceiveRmaDeviceCheckIns> {
  return apiFetch(`/api/shipping/inbound/receive/rma-device-check-ins/${encodeURIComponent(input.checkInId)}`, {
    method: "DELETE",
    query: { rma_number: input.rmaNumber.trim() },
  });
}

export async function matchInboundReceiveRmaDevices(
  deviceSerials: string[],
  rmaNumber?: string,
): Promise<{
  matches: InboundReceiveRmaDeviceMatch[];
  unmatchedSerials: string[];
  confirmed: InboundReceiveRmaDeviceMatch | null;
  finish: InboundReceiveRmaFinishEvaluation | null;
}> {
  return apiFetch("/api/shipping/inbound/receive/rma-device-match", {
    method: "POST",
    body: { device_serials: deviceSerials, rma_number: rmaNumber?.trim() ?? "" },
  });
}

export type InboundReceiveRmaFinishEvaluation = {
  rmaId: string;
  rmaNumber: string;
  orderedDeviceCount: number;
  checkedInCount: number;
  listedDeviceCount: number;
  quantityDiscrepancy: boolean;
  countChecksOut: boolean;
  canFinish: boolean;
};

export type ReceiveRmaQuickSearchRow = {
  id: number;
  rmaNumber: string;
  customerName: string | null;
  complaint: string | null;
};

export async function fetchReceiveRmaQuickSearch(query: string): Promise<ReceiveRmaQuickSearchRow[]> {
  const data = await apiFetch<{ rows: ReceiveRmaQuickSearchRow[] }>("/api/quality/rmas/quick-search", {
    query: { q: query.trim(), limit: "12" },
  });
  return data.rows;
}

export async function associateReceiveArrivalPurchaseOrder(input: {
  scan: string;
  purchaseOrderId: number;
}): Promise<{
  resolved_source: {
    source_type: InboundSourceType;
    source_id: string;
    source_number: string | null;
  };
  package_id: string;
  package_no: number;
}> {
  return apiFetch("/api/shipping/inbound/receive/arrival-packages/associate-purchase-order", {
    method: "POST",
    body: {
      scan: input.scan.trim(),
      purchase_order_id: input.purchaseOrderId,
    },
  });
}

export async function scanArrivalPackageOntoReceipt(
  receiptId: string,
  scan: string,
): Promise<{
  linked: boolean;
  package_no: number;
  arrival_packages: InboundReceiptArrivalPackage[];
  receipt: InboundReceipt | null;
}> {
  return apiFetch<{
    linked: boolean;
    package_no: number;
    arrival_packages: InboundReceiptArrivalPackage[];
    receipt: InboundReceipt | null;
  }>(`/api/shipping/inbound/receipts/${encodeURIComponent(receiptId)}/arrival-packages/scan`, {
    method: "POST",
    body: { scan: scan.trim() },
  });
}

export async function closeInboundSubcontainer(
  receiptId: string,
  unitKind: string,
  options?: {
    printer?: string;
    printLines?: Array<{
      sku: string;
      quantity: number;
      description?: string | null;
      source_item_id?: string | null;
      inbound_receipt_item_id?: string | number | null;
    }>;
  },
): Promise<{
  subcontainer: InboundReceiptSubcontainer;
  subcontainers: InboundReceiptSubcontainer[];
  receipt: InboundReceipt | null;
  print?: InventoryLabelBatchResult;
}> {
  const body: {
    unit_kind: string;
    printer?: string;
    print_lines?: Array<{
      sku: string;
      quantity: number;
      description: string | null;
      source_item_id?: string;
      inbound_receipt_item_id?: string | number | null;
    }>;
  } = { unit_kind: unitKind };
  const printer = options?.printer?.trim() ?? "";
  if (printer !== "") {
    body.printer = printer;
    if (options?.printLines != null) {
      body.print_lines = options.printLines.map((line) => ({
        sku: line.sku,
        quantity: line.quantity,
        description: line.description ?? null,
        source_item_id: line.source_item_id ?? undefined,
        inbound_receipt_item_id: line.inbound_receipt_item_id ?? undefined,
      }));
    }
  }
  return apiFetch<{
    subcontainer: InboundReceiptSubcontainer;
    subcontainers: InboundReceiptSubcontainer[];
    receipt: InboundReceipt | null;
    print?: InventoryLabelBatchResult;
  }>(`/api/shipping/inbound/receipts/${encodeURIComponent(receiptId)}/subcontainers/close`, {
    method: "POST",
    body,
  });
}

export async function patchInboundSubcontainerUnitKind(
  receiptId: string,
  subcontainerId: string,
  unitKind: string,
): Promise<{ subcontainer: InboundReceiptSubcontainer }> {
  return apiFetch<{ subcontainer: InboundReceiptSubcontainer }>(
    `/api/shipping/inbound/receipts/${encodeURIComponent(receiptId)}/subcontainers/${encodeURIComponent(subcontainerId)}`,
    {
      method: "PATCH",
      body: { unit_kind: unitKind },
    },
  );
}

export async function unsealInboundSubcontainer(
  receiptId: string,
  subcontainerId: string,
): Promise<{
  unsealed_subcontainer_no: number;
  labels_voided: number;
  subcontainers: InboundReceiptSubcontainer[];
  receipt: InboundReceipt | null;
}> {
  return apiFetch<{
    unsealed_subcontainer_no: number;
    labels_voided: number;
    subcontainers: InboundReceiptSubcontainer[];
    receipt: InboundReceipt | null;
  }>(
    `/api/shipping/inbound/receipts/${encodeURIComponent(receiptId)}/subcontainers/${encodeURIComponent(subcontainerId)}/unseal`,
    { method: "POST" },
  );
}

export async function patchInboundReceiptOpenSubcontainerUnitKind(
  receiptId: string,
  unitKind: InboundArrivalUnitKind,
): Promise<{ open_subcontainer_unit_kind: string }> {
  return apiFetch<{ open_subcontainer_unit_kind: string }>(
    `/api/shipping/inbound/receipts/${encodeURIComponent(receiptId)}/open-subcontainer-unit-kind`,
    {
      method: "PATCH",
      body: { unit_kind: unitKind },
    },
  );
}

export async function fetchInboundDetail(
  sourceType: InboundSourceType,
  sourceId: string,
  arrivalPackageId?: string | null,
): Promise<InboundDetailResponse> {
  const query: Record<string, string> = {};
  if (arrivalPackageId != null && arrivalPackageId.trim() !== "") {
    query.arrival_package_id = arrivalPackageId.trim();
  }
  return apiFetch<InboundDetailResponse>(
    `/api/shipping/inbound/${sourceType}/${encodeURIComponent(sourceId)}`,
    { query },
  );
}

export interface InboundReceiptReviewLine {
  sku: string;
  description: string | null;
  received_quantity: string;
  unit_of_measure: string | null;
}

export interface InboundReceiptReviewContainerItem {
  source_item_id: string;
  sku: string;
  quantity: string;
}

export interface InboundReceiptReviewContainer {
  id: string;
  subcontainer_no: number;
  unit_kind: string;
  status: string;
  items: InboundReceiptReviewContainerItem[];
}

export interface InboundReceiptReview {
  id: string;
  status: string;
  received_at: string | null;
  source_type: string;
  source_id: string;
  source_number: string | null;
  notes: string | null;
  lines: InboundReceiptReviewLine[];
  subcontainers: InboundReceiptReviewContainer[];
}

export async function fetchInboundReceiptReview(receiptId: string): Promise<InboundReceiptReview> {
  const data = await apiFetch<{ receipt: InboundReceiptReview }>(
    `/api/shipping/inbound/receipts/${encodeURIComponent(receiptId)}`,
  );
  return data.receipt;
}

export async function saveInboundReceipt(input: InboundReceiptInput): Promise<InboundReceipt | null> {
  const data = await apiFetch<{ receipt: InboundReceipt | null }>("/api/shipping/inbound/receipts", {
    method: "POST",
    body: input,
  });
  return data.receipt;
}

export type InboundLineMpnVerifyResult =
  | { status: "not_required" }
  | { status: "matched"; approved_component_id: number; matched_mpn?: string }
  | { status: "unmatched" }
  | { status: "choose_manufacturer"; manufacturers: string[]; matched_mpn?: string };

export async function verifyInboundLineMpn(input: {
  source_type: InboundSourceType;
  source_id: number;
  source_item_id: number;
  scanned_mpn: string;
  manufacturer_name?: string | null;
}): Promise<InboundLineMpnVerifyResult> {
  return apiFetch<InboundLineMpnVerifyResult>("/api/shipping/inbound/verify-line-mpn", {
    method: "POST",
    body: input,
  });
}

export async function fetchAmlManufacturerChoices(input: {
  sku: string;
  scanned_mpn: string;
}): Promise<InboundLineMpnVerifyResult> {
  return apiFetch<InboundLineMpnVerifyResult>("/api/shipping/inbound/manufacturer-choices", {
    method: "POST",
    body: input,
  });
}

export async function createInboundAdHocReceiptLine(
  receiptId: string,
  input: {
    line_kind: "unlisted_sku" | "non_sku";
    sku?: string | null;
    description?: string | null;
    received_quantity: number;
  },
): Promise<{ receipt_item_id: string; line: InboundLineRow }> {
  return apiFetch<{ receipt_item_id: string; line: InboundLineRow }>(
    `/api/shipping/inbound/receipts/${encodeURIComponent(receiptId)}/ad-hoc-lines`,
    { method: "POST", body: input },
  );
}

export async function patchInboundAdHocReceiptLineQuantity(
  receiptItemId: string,
  receivedQuantity: number,
): Promise<void> {
  await apiFetch<{ ok: true }>(
    `/api/shipping/inbound/receipt-items/${encodeURIComponent(receiptItemId)}/ad-hoc-quantity`,
    { method: "PATCH", body: { received_quantity: receivedQuantity } },
  );
}

export async function splitInboundDamagedOrderLine(
  receiptId: string,
  input: {
    source_item_id: number;
    good_quantity: number;
    damaged_quantity: number;
    notes?: string | null;
  },
): Promise<{ ok: true; receipt_item_id: string }> {
  return apiFetch<{ ok: true; receipt_item_id: string }>(
    `/api/shipping/inbound/receipts/${encodeURIComponent(receiptId)}/damaged-order-line`,
    { method: "POST", body: input },
  );
}

export async function claimInboundReceiveCrate(input: {
  inbound_arrival_package_id: number;
  job_key: string;
}): Promise<void> {
  await apiFetch<{ ok: boolean }>("/api/shipping/inbound/container-labor/open-crate", {
    method: "POST",
    body: input,
  });
}

export async function closeInboundContainerLaborVisit(input: {
  inbound_arrival_package_id: number;
  job_key: string;
  armed: boolean;
  visit_started_at_ms: number;
  last_activity_at_ms: number;
  idle_timeout: boolean;
}): Promise<{ recorded: boolean; allocated_seconds: number }> {
  return apiFetch<{ recorded: boolean; allocated_seconds: number }>(
    "/api/shipping/inbound/container-labor/close-visit",
    { method: "POST", body: input },
  );
}

export async function uploadInboundPackingSlip(receiptId: string, file: File): Promise<InboundReceipt> {
  const formData = new FormData();
  formData.append("file", file);
  const data = await apiUploadFormData<{ receipt: InboundReceipt }>(
    `/api/shipping/inbound/receipts/${encodeURIComponent(receiptId)}/packing-slips`,
    formData,
  );
  return data.receipt;
}

export async function fetchPackingSlipUrl(
  receiptId: string,
  slipId?: string,
): Promise<{ signedUrl: string; filename: string | null; slipId?: string }> {
  if (slipId != null && slipId.trim() !== "") {
    return apiFetch<{ signedUrl: string; filename: string | null; slipId: string }>(
      `/api/shipping/inbound/receipts/${encodeURIComponent(receiptId)}/packing-slips/${encodeURIComponent(slipId)}`,
    );
  }
  return apiFetch<{ signedUrl: string; filename: string | null; slipId?: string }>(
    `/api/shipping/inbound/receipts/${encodeURIComponent(receiptId)}/packing-slips/latest`,
  );
}

export async function deleteInboundPackingSlip(receiptId: string, slipId: string): Promise<InboundReceipt | null> {
  const data = await apiFetch<{ receipt: InboundReceipt | null }>(
    `/api/shipping/inbound/receipts/${encodeURIComponent(receiptId)}/packing-slips/${encodeURIComponent(slipId)}`,
    { method: "DELETE" },
  );
  return data.receipt;
}

export async function deleteAllInboundPackingSlips(receiptId: string): Promise<InboundReceipt | null> {
  const data = await apiFetch<{ receipt: InboundReceipt | null }>(
    `/api/shipping/inbound/receipts/${encodeURIComponent(receiptId)}/packing-slips`,
    { method: "DELETE" },
  );
  return data.receipt;
}

export function inboundReceiptPackingSlips(receipt: InboundReceipt | null | undefined): InboundPackingSlip[] {
  if (receipt?.packing_slips != null && receipt.packing_slips.length > 0) {
    return receipt.packing_slips;
  }
  if (receipt?.packing_slip_filename?.trim()) {
    return [
      {
        id: "legacy",
        filename: receipt.packing_slip_filename,
        uploaded_at: receipt.packing_slip_uploaded_at ?? receipt.updated_at,
      },
    ];
  }
  return [];
}

export interface InboundPutAwayRouting {
  holding_line_count: number;
  to_stock_line_count: number;
  instruction: string;
}

export interface InboundSubmitResult {
  receipt: InboundReceipt;
  /** Lines queued for Holding inspection after submit. */
  holding_tasks_created: number;
  put_away_routing: InboundPutAwayRouting;
}

function normalizePutAwayRouting(
  value: InboundPutAwayRouting | null | undefined,
): InboundPutAwayRouting {
  if (value != null && typeof value.instruction === "string") {
    return {
      holding_line_count: Number(value.holding_line_count) || 0,
      to_stock_line_count: Number(value.to_stock_line_count) || 0,
      instruction: value.instruction,
    };
  }
  return {
    holding_line_count: 0,
    to_stock_line_count: 0,
    instruction: "No put-away lines on this receipt.",
  };
}

export async function submitInboundReceipt(receiptId: string): Promise<InboundSubmitResult> {
  const data = await apiFetch<Partial<InboundSubmitResult> & { receipt: InboundReceipt }>(
    `/api/shipping/inbound/receipts/${encodeURIComponent(receiptId)}/submit`,
    { method: "POST" },
  );
  return {
    receipt: data.receipt,
    holding_tasks_created: data.holding_tasks_created ?? 0,
    put_away_routing: normalizePutAwayRouting(data.put_away_routing),
  };
}

export interface InboundFinishArrivalContainerResult extends InboundSubmitResult {
  finished_package_ids: string[];
  arrival_marked_received: boolean;
}

export async function finishInboundReceiptArrivalContainer(
  receiptId: string,
): Promise<InboundFinishArrivalContainerResult> {
  const data = await apiFetch<
    Partial<InboundFinishArrivalContainerResult> & { receipt: InboundReceipt }
  >(`/api/shipping/inbound/receipts/${encodeURIComponent(receiptId)}/finish-arrival-container`, {
    method: "POST",
  });
  return {
    receipt: data.receipt,
    holding_tasks_created: data.holding_tasks_created ?? 0,
    put_away_routing: normalizePutAwayRouting(data.put_away_routing),
    finished_package_ids: data.finished_package_ids ?? [],
    arrival_marked_received: data.arrival_marked_received ?? false,
  };
}

export async function printInboundInventoryLabel(input: {
  sku: string;
  quantity: number;
  printer: string;
  description?: string | null;
}): Promise<{ printed: true; sku: string; quantity: number }> {
  return apiFetch<{ printed: true; sku: string; quantity: number }>(
    "/api/shipping/inbound/inventory-label/print",
    {
      method: "POST",
      body: {
        sku: input.sku,
        quantity: input.quantity,
        printer: input.printer,
        description: input.description ?? null,
      },
    },
  );
}

export async function printInboundInventoryLocationLabel(input: {
  sku: string;
  printer: string;
  description?: string | null;
}): Promise<{ printed: true; sku: string; location_id: number; scan_url: string }> {
  return apiFetch<{ printed: true; sku: string; location_id: number; scan_url: string }>(
    "/api/shipping/inbound/inventory-label/print-location",
    {
      method: "POST",
      body: {
        sku: input.sku,
        printer: input.printer,
        description: input.description ?? null,
      },
    },
  );
}

export interface InventoryLabelBatchResultLine {
  sku: string;
  quantity: number;
  ok: boolean;
  error?: string;
}

export interface InventoryLabelBatchResult {
  printed: number;
  failed: number;
  results: InventoryLabelBatchResultLine[];
}

/** Print one 4x2 inventory label per counted line in a single action — no SKU re-entry. */
export async function printInboundInventoryLabelBatch(input: {
  printer: string;
  lines: Array<{
    sku: string;
    quantity: number;
    description?: string | null;
    source_item_id?: string | null;
    inbound_receipt_item_id?: string | number | null;
  }>;
}): Promise<InventoryLabelBatchResult> {
  return apiFetch<InventoryLabelBatchResult>("/api/shipping/inbound/inventory-label/print-batch", {
    method: "POST",
    body: {
      printer: input.printer,
      lines: input.lines.map((line) => ({
        sku: line.sku,
        quantity: line.quantity,
        description: line.description ?? null,
        source_item_id: line.source_item_id ?? undefined,
        inbound_receipt_item_id: line.inbound_receipt_item_id ?? undefined,
      })),
    },
  });
}

// --- Dock arrival log / inbound queue (vendor + box count; blind/no-PO friendly) ---

export type InboundArrivalStatus = "open" | "received" | "cancelled";

export interface InboundDockShipmentUnit {
  package_id: string;
  arrival_id: string;
  package_no: number;
  unit_kind: InboundArrivalUnitKind;
  carrier: string | null;
  tracking: string | null;
  po_number: string | null;
  logged_at: string;
  scan_token: string;
  scan_label: string;
}

export interface InboundDockShipment {
  id: string;
  vendor_name: string;
  po_number: string | null;
  expected_tracking: string | null;
  expected_arrival: string | null;
  incoming_line_count: number | null;
  linked_source_type: InboundSourceType | null;
  linked_source_id: string | null;
  receive_route: import("./inboundReceiveRoute").InboundReceiveRoute | null;
  notes: string | null;
  unit_count: number;
  unit_kind: InboundArrivalUnitKind;
  has_expectation: boolean;
  units: InboundDockShipmentUnit[];
}

/** @deprecated Use InboundDockShipment for the dock queue list. */
export type InboundDockQueueKind = "arrival" | "expected";
/** @deprecated Use InboundDockShipment for the dock queue list. */
export type InboundDockQueueStatus = InboundArrivalStatus | "expected";

/** @deprecated Use InboundDockShipment for the dock queue list. */
export interface InboundArrival {
  id: string;
  kind: InboundDockQueueKind;
  vendor_name: string;
  box_count: number | null;
  carrier: string | null;
  tracking: string | null;
  po_number: string | null;
  notes: string | null;
  status: InboundDockQueueStatus;
  linked_source_type: InboundSourceType | null;
  linked_source_id: string | null;
  arrived_at: string | null;
  received_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  expected_arrival: string | null;
  incoming_line_count: number | null;
  merged_from_incoming: boolean;
}

export interface LogInboundArrivalPrefill {
  vendor_name?: string;
  po_number?: string | null;
  po_numbers?: string[] | null;
  tracking?: string;
  linked_source_type?: InboundSourceType | null;
  linked_source_id?: number | null;
}

export interface LogInboundArrivalInput {
  vendor_name: string;
  box_count: number;
  unit_kind?: InboundArrivalUnitKind;
  carrier?: string;
  tracking?: string;
  no_bol_or_label?: boolean;
  po_number?: string | null;
  po_numbers?: string[];
  transfer_number?: string | null;
  notes?: string | null;
  linked_source_type?: InboundSourceType | null;
  linked_source_id?: number | null;
  receive_route?: import("./inboundReceiveRoute").InboundReceiveRoute | null;
}

export const INBOUND_ARRIVALS_QUERY_KEY = ["shipping", "inbound", "arrivals"] as const;

export async function fetchInboundArrivals(): Promise<InboundDockShipment[]> {
  const data = await apiFetch<{ rows: InboundDockShipment[] }>("/api/shipping/inbound/arrivals");
  return data.rows;
}

export async function logInboundArrival(
  input: LogInboundArrivalInput,
): Promise<{ arrival: InboundArrival; packageIds: string[] }> {
  const data = await apiFetch<{ arrival: InboundArrival; package_ids?: string[] }>(
    "/api/shipping/inbound/arrivals",
    {
      method: "POST",
      body: input,
    },
  );
  return {
    arrival: data.arrival,
    packageIds: data.package_ids ?? [],
  };
}

export async function printInboundArrivalPackageLabels(input: {
  arrivalId: string;
  printer: string;
  packageId?: string;
  packageIds?: string[];
}): Promise<{ printed: number; arrivalId: string }> {
  const packageIds =
    input.packageIds != null && input.packageIds.length > 0
      ? input.packageIds
      : input.packageId != null && input.packageId !== ""
        ? [input.packageId]
        : undefined;
  return apiFetch<{ printed: number; arrivalId: string }>(
    `/api/shipping/inbound/arrivals/${encodeURIComponent(input.arrivalId)}/package-labels/print`,
    {
      method: "POST",
      body: {
        printer: input.printer,
        ...(packageIds != null ? { package_ids: packageIds } : {}),
      },
    },
  );
}

export async function cancelInboundArrivalPackage(packageId: string): Promise<{
  ok: boolean;
  packageId: string;
  arrivalId: string;
  arrivalCancelled: boolean;
}> {
  return apiFetch(`/api/shipping/inbound/arrival-packages/${encodeURIComponent(packageId)}/cancel`, {
    method: "POST",
    body: {},
  });
}

export async function updateInboundArrival(
  id: string,
  patch: Partial<
    Omit<
      InboundArrival,
      | "id"
      | "kind"
      | "arrived_at"
      | "received_at"
      | "created_at"
      | "updated_at"
      | "expected_arrival"
      | "incoming_line_count"
      | "merged_from_incoming"
    >
  >,
): Promise<InboundArrival> {
  const data = await apiFetch<{ arrival: InboundArrival }>(
    `/api/shipping/inbound/arrivals/${encodeURIComponent(id)}`,
    { method: "PATCH", body: patch },
  );
  return data.arrival;
}

// --- DEPRECATED: per-line receiving discrepancies (routes commented out in shipping-inbound-routes.ts).
// Types kept for historical UI files under ui/src/pages/shipping/InboundDiscrepancy*.tsx (not routed).
// New work: inbound_holding_task via fetchHoldingTasks / recordHoldingInspect.

export type InboundDiscrepancyType = "short" | "over" | "damaged" | "sku_mismatch" | "quality_dispute";
export type InboundDiscrepancyStatus = "open" | "deferred" | "resolved" | "cancelled";
export type InboundDiscrepancyResolutionAction =
  | "keep_free"
  | "amend_pay"
  | "return_to_vendor"
  | "scrap"
  | "remap_sku"
  | "accept_short"
  | "release"
  | "reject";

/**
 * Mirrors the backend BLOCKING_DISCREPANCY_TYPES: these block submit while open
 * and can only be REOPENED while the receipt is still a draft (server 409s
 * otherwise) — the review queue hides Reopen for them on non-draft receipts.
 */
export const BLOCKING_DISCREPANCY_TYPES: ReadonlySet<InboundDiscrepancyType> = new Set([
  "sku_mismatch",
  "damaged",
]);

/** Mirrors the backend RESOLUTION_ACTIONS_BY_TYPE (Ryan-approved 2026-06-18). */
export const RESOLUTION_ACTIONS_BY_TYPE: Record<InboundDiscrepancyType, InboundDiscrepancyResolutionAction[]> = {
  short: ["accept_short", "reject"],
  over: ["keep_free", "amend_pay", "return_to_vendor"],
  damaged: ["scrap", "return_to_vendor", "release"],
  sku_mismatch: ["remap_sku", "reject", "return_to_vendor"],
  quality_dispute: ["release", "reject", "return_to_vendor"],
};

export const DISCREPANCY_TYPE_LABELS: Record<InboundDiscrepancyType, string> = {
  short: "Short",
  over: "Over",
  damaged: "Damaged",
  sku_mismatch: "SKU mismatch",
  quality_dispute: "Quality dispute",
};

export const RESOLUTION_ACTION_LABELS: Record<InboundDiscrepancyResolutionAction, string> = {
  keep_free: "Keep (no pay)",
  amend_pay: "Amend PO & pay",
  return_to_vendor: "Return to vendor",
  scrap: "Scrap",
  remap_sku: "Remap SKU",
  accept_short: "Accept short",
  release: "Release",
  reject: "Reject",
};

export interface InboundReceiptDiscrepancy {
  id: string;
  inbound_receipt_item_id: string;
  discrepancy_type: InboundDiscrepancyType;
  quantity: string;
  status: InboundDiscrepancyStatus;
  resolution_action: InboundDiscrepancyResolutionAction | null;
  resolution_notes: string | null;
  notes: string | null;
  /** sku_mismatch: the SKU the vendor / packing slip labeled (what arrived). */
  observed_sku: string | null;
  /** sku_mismatch resolved via remap_sku: the correct SGS SKU. */
  remap_target_sku: string | null;
  created_by: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface FlagDiscrepancyInput {
  inbound_receipt_item_id: number;
  discrepancy_type: InboundDiscrepancyType;
  quantity: number;
  notes?: string | null;
  /** Only for sku_mismatch: the vendor / packing-slip SKU that arrived. */
  observed_sku?: string | null;
}

export interface DiscrepancyPatch {
  status?: InboundDiscrepancyStatus;
  resolution_action?: InboundDiscrepancyResolutionAction | null;
  resolution_notes?: string | null;
  notes?: string | null;
  quantity?: number;
  /** Required when resolving a sku_mismatch via remap_sku. */
  remap_target_sku?: string | null;
}

export interface InboundSkuOption {
  sku: string;
  description: string | null;
  variant: string | null;
  category: string | null;
}

export interface InboundDiscrepancyEvent {
  id: string;
  inbound_receipt_discrepancy_id: string;
  event_type: "created" | "updated";
  from_status: string | null;
  to_status: string;
  resolution_action: string | null;
  quantity: string | null;
  notes: string | null;
  actor: string | null;
  created_at: string;
}

/** One row of the purchasing review queue: a discrepancy joined to its receipt + source. */
export interface InboundDiscrepancyReviewRow {
  id: string;
  discrepancy_type: InboundDiscrepancyType;
  quantity: string;
  status: InboundDiscrepancyStatus;
  resolution_action: InboundDiscrepancyResolutionAction | null;
  resolution_notes: string | null;
  notes: string | null;
  observed_sku: string | null;
  remap_target_sku: string | null;
  created_at: string;
  resolved_at: string | null;
  sku: string;
  receipt_id: string;
  receipt_status: string;
  source_type: InboundSourceType;
  source_id: string;
  source_number: string | null;
  source_status: string | null;
  supplier_or_origin: string | null;
  /** False for deleted sources or legacy source types the receive page can't open. */
  source_exists: boolean;
}

/*
export async function fetchInboundDiscrepancies(statusCsv?: string): Promise<InboundDiscrepancyReviewRow[]> {
  const data = await apiFetch<{ rows: InboundDiscrepancyReviewRow[] }>("/api/shipping/inbound/discrepancies", {
    query: statusCsv ? { status: statusCsv } : undefined,
  });
  return data.rows;
}

export async function fetchReceiptDiscrepancies(receiptId: string): Promise<InboundReceiptDiscrepancy[]> {
  const data = await apiFetch<{ rows: InboundReceiptDiscrepancy[] }>(
    `/api/shipping/inbound/receipts/${encodeURIComponent(receiptId)}/discrepancies`,
  );
  return data.rows;
}

export async function flagDiscrepancy(input: FlagDiscrepancyInput): Promise<InboundReceiptDiscrepancy> {
  const data = await apiFetch<{ discrepancy: InboundReceiptDiscrepancy }>("/api/shipping/inbound/discrepancies", {
    method: "POST",
    body: input,
  });
  return data.discrepancy;
}

export async function updateDiscrepancy(id: string, patch: DiscrepancyPatch): Promise<InboundReceiptDiscrepancy> {
  const data = await apiFetch<{ discrepancy: InboundReceiptDiscrepancy }>(
    `/api/shipping/inbound/discrepancies/${encodeURIComponent(id)}`,
    { method: "PATCH", body: patch },
  );
  return data.discrepancy;
}
*/

/** SGS SKU search for dock ad-hoc / unlisted SKU lines. */
export async function searchInboundSkus(query: string): Promise<InboundSkuOption[]> {
  const data = await apiFetch<{ rows: InboundSkuOption[] }>("/api/shipping/inbound/sku-search", {
    query: { q: query },
  });
  return data.rows;
}

export interface InboundAmlLookupRow {
  cpn: string;
  manufacturer_name: string | null;
  mpn: string;
  description: string | null;
}

/** Full manufacturer part number to approved AML customer part numbers (CPN). */
export async function lookupInboundAmlByMpn(mpn: string): Promise<InboundAmlLookupRow[]> {
  const data = await apiFetch<{ rows: InboundAmlLookupRow[] }>("/api/shipping/inbound/aml-lookup", {
    query: { mpn: mpn.trim() },
  });
  return data.rows;
}

/*
export async function setReceiptItemSecondCountRequired(
  itemId: string,
  requiresSecondCount: boolean,
): Promise<InboundReceiptItemFlag> {
  const data = await apiFetch<{ item: InboundReceiptItemFlag }>(
    `/api/shipping/inbound/receipt-items/${encodeURIComponent(itemId)}`,
    { method: "PATCH", body: { requires_second_count: requiresSecondCount } },
  );
  return data.item;
}
*/

export interface InboundArrivalFieldOptions {
  vendors: string[];
  carriers: string[];
  purchase_orders: string[];
  transfer_orders: string[];
}

/** Autocomplete sources for the dock arrival form (vendor and carrier suggestions). */
export async function fetchInboundArrivalFieldOptions(): Promise<InboundArrivalFieldOptions> {
  return apiFetch<InboundArrivalFieldOptions>("/api/shipping/inbound/arrival-field-options");
}

export interface InboundArrivalLabelScanResult {
  vendor_name: string | null;
  vendor_matched?: boolean;
  po_number: string | null;
  po_matched?: boolean;
  transfer_number: string | null;
  transfer_matched?: boolean;
  suggested_receive_route?: import("./inboundReceiveRoute").InboundReceiveRoute | null;
  box_count: number | null;
  carrier: string | null;
  tracking: string | null;
  document_kind: "bol" | "shipping_label" | "unknown";
}

export type InboundMpnLabelScanField =
  | "manufacturer_part_number"
  | "model"
  | "part_number"
  | "customer_part_number"
  | "other";

export type InboundMpnLabelScanConfidence = "high" | "medium" | "low";

export interface InboundMpnLabelScanCandidate {
  value: string;
  field: InboundMpnLabelScanField;
  confidence: InboundMpnLabelScanConfidence;
}

export interface InboundMpnLabelScanResult {
  candidates: InboundMpnLabelScanCandidate[];
}

/** Synchronous Gemini read of a BOL or UPS/FedEx label photo. The tablet waits on this response. */
export async function scanInboundArrivalLabel(file: File): Promise<InboundArrivalLabelScanResult> {
  const formData = new FormData();
  formData.append("file", file);
  return apiUploadFormData<InboundArrivalLabelScanResult>(
    "/api/shipping/inbound/arrivals/scan-label",
    formData,
    { timeoutMs: 45_000 },
  );
}

/** Synchronous Gemini read of model / manufacturer part number strings from a component label photo. */
export async function scanInboundMpnLabel(file: File): Promise<InboundMpnLabelScanResult> {
  const formData = new FormData();
  formData.append("file", file);
  return apiUploadFormData<InboundMpnLabelScanResult>(
    "/api/shipping/inbound/scan-mpn-label",
    formData,
    { timeoutMs: 45_000 },
  );
}

export interface InboundAttentionCounts {
  /** Open Holding inspections (open + mismatch). */
  holding: number;
  /** @deprecated use holding */
  discrepancies: number;
  /** @deprecated always 0 */
  second_counts: number;
}

/** Honest count(*) badges for the queue-page header links. */
export async function fetchInboundAttentionCounts(): Promise<InboundAttentionCounts> {
  const data = await apiFetch<InboundAttentionCounts>("/api/shipping/inbound/attention-counts");
  return {
    holding: data.holding ?? data.discrepancies ?? 0,
    discrepancies: data.discrepancies ?? data.holding ?? 0,
    second_counts: data.second_counts ?? 0,
  };
}

export type InboundHoldingReason =
  | "iqc"
  | "damaged"
  | "sku_mismatch"
  | "packing_slip_mismatch"
  | "invoice_receipt_mismatch"
  | "waiting_on_invoice"
  | "unknown_mpn"
  | "awaiting_wofg"
  | "purchase_order_short"
  | "manufacturer_part_mismatch";
export type InboundHoldingTaskStatus =
  | "open"
  | "verified"
  | "mismatch"
  | "certified_mismatch"
  | "deferred"
  | "resolved"
  | "cancelled";

export interface InboundHoldingTask {
  id: string;
  reason: InboundHoldingReason;
  status: InboundHoldingTaskStatus;
  inbound_receipt_item_id: string;
  sku: string | null;
  scanned_mpn?: string | null;
  quantity: string;
  reference_quantity: string | null;
  notes: string | null;
  observed_sku: string | null;
  source_type: string;
  source_id: string;
  source_number: string | null;
  arrived_at: string | null;
  received_at: string | null;
  first_count_quantity: string;
  first_count_by: string | null;
  inspect_quantity: string | null;
  inspect_by: string | null;
  inspect_at: string | null;
  iqc_status: string;
  created_at: string;
  line_description?: string | null;
  /** True when a linked accounts payable invoice already has a Megaventory purchase invoice. */
  purchase_invoice_in_megaventory?: boolean;
  receive_container_id?: string | null;
  receive_subcontainer_no?: number | null;
  receive_unit_kind?: string | null;
  containers?: InboundHoldingContainer[];
}

export interface InboundHoldingContainer {
  id: string;
  receive_container_id?: string;
  subcontainer_no: number;
  unit_kind: string | null;
  quantity: string;
  first_count_quantity?: string | null;
  label_code?: string | null;
}

export const HOLDING_REASON_LABELS: Record<InboundHoldingReason, string> = {
  iqc: "IQC rule",
  damaged: "Damaged",
  sku_mismatch: "Wrong SKU",
  packing_slip_mismatch: "Packing slip mismatch",
  invoice_receipt_mismatch: "Invoice mismatch",
  waiting_on_invoice: "Waiting on invoice",
  unknown_mpn: "Unknown manufacturer part",
  awaiting_wofg: "Waiting on finished goods",
  purchase_order_short: "Purchase order short",
  manufacturer_part_mismatch: "Manufacturer part mismatch",
};

/** Holding inspection queue (open + mismatch by default). */
export async function fetchHoldingTasks(input?: {
  status?: string;
  scan?: string;
}): Promise<InboundHoldingTask[]> {
  const data = await apiFetch<{ rows: InboundHoldingTask[] }>("/api/shipping/inbound/holding-tasks", {
    query: {
      ...(input?.status ? { status: input.status } : {}),
      ...(input?.scan != null && input.scan.trim() !== "" ? { scan: input.scan.trim() } : {}),
    },
  });
  return data.rows;
}

/** Record inspect/recount on an open holding task (different person than Receive). */
export async function recordHoldingInspect(taskId: string, quantity: number): Promise<InboundHoldingTask> {
  const data = await apiFetch<{ task: InboundHoldingTask }>(
    `/api/shipping/inbound/holding-tasks/${encodeURIComponent(taskId)}/inspect`,
    { method: "POST", body: { quantity } },
  );
  return data.task;
}

/** Shipping administrator certifies that the inspection count still disagrees with the first count. */
export async function certifyHoldingMismatch(taskId: string): Promise<InboundHoldingTask> {
  const data = await apiFetch<{ task: InboundHoldingTask }>(
    `/api/shipping/inbound/holding-tasks/${encodeURIComponent(taskId)}/certify-mismatch`,
    { method: "POST", body: {} },
  );
  return data.task;
}

/** Update one sealed container on a holding line and reprint that container's label. */
export async function updateHoldingContainerQuantity(
  taskId: string,
  containerId: string,
  quantity: number,
  printer: string,
  correction?: { sku?: string | null; scannedMpn?: string | null; manufacturerName?: string | null },
): Promise<InboundHoldingTask> {
  const sku = correction?.sku?.trim() ?? "";
  const scannedMpn = correction?.scannedMpn?.trim() ?? "";
  const data = await apiFetch<{ task: InboundHoldingTask }>(
    `/api/shipping/inbound/holding-tasks/${encodeURIComponent(taskId)}/containers/${encodeURIComponent(containerId)}`,
    {
      method: "POST",
      body: {
        quantity,
        printer,
        ...(sku !== "" ? { sku } : {}),
        ...(scannedMpn !== "" ? { scanned_mpn: scannedMpn } : {}),
        ...(correction?.manufacturerName?.trim()
          ? { manufacturer_name: correction.manufacturerName.trim() }
          : {}),
      },
    },
  );
  return data.task;
}

export interface HoldingRecountEntry {
  id: string;
  kind: "first_count" | "recount";
  quantity_before: number | null;
  quantity_after: number;
  actor_display_name: string | null;
  at: string;
}

export async function fetchHoldingContainerHistory(
  taskId: string,
  containerId: string,
): Promise<{ label_code: string | null; entries: HoldingRecountEntry[] }> {
  return apiFetch(`/api/shipping/inbound/holding-tasks/${encodeURIComponent(taskId)}/containers/${encodeURIComponent(containerId)}/history`);
}

/** Release a verified invoice hold from Holding to To stock. */
export async function releaseHoldingTaskToStock(taskId: string): Promise<InboundHoldingTask> {
  const data = await apiFetch<{ task: InboundHoldingTask }>(
    `/api/shipping/inbound/holding-tasks/${encodeURIComponent(taskId)}/release-to-stock`,
    { method: "POST", body: {} },
  );
  return data.task;
}

/** Close a finished-goods hold after the work-order receipt. Does not add on-hand or send the line to To stock. */
export async function resolveAwaitingWofgHoldingTask(taskId: string): Promise<InboundHoldingTask> {
  const data = await apiFetch<{ task: InboundHoldingTask }>(
    `/api/shipping/inbound/holding-tasks/${encodeURIComponent(taskId)}/resolve-wofg`,
    { method: "POST", body: {} },
  );
  return data.task;
}

export type InboundIqcRuleMatchType = "sku" | "supplier" | "sku_supplier";

export interface InboundIqcRule {
  id: string;
  match_type: InboundIqcRuleMatchType;
  sku: string | null;
  supplier_id: string | null;
  active: boolean;
  note: string | null;
  supplier_name: string | null;
  created_at: string;
}

export interface InboundIqcRuleOptions {
  suppliers: { id: string; name: string | null }[];
}

export async function fetchInboundIqcRules(): Promise<InboundIqcRule[]> {
  const data = await apiFetch<{ rows: InboundIqcRule[] }>("/api/shipping/inbound/iqc-rules");
  return data.rows;
}

export async function fetchInboundIqcRuleOptions(): Promise<InboundIqcRuleOptions> {
  return apiFetch<InboundIqcRuleOptions>("/api/shipping/inbound/iqc-rule-options");
}

export async function createInboundIqcRule(input: {
  match_type: InboundIqcRuleMatchType;
  sku?: string;
  supplier_id?: number;
}): Promise<InboundIqcRule> {
  const data = await apiFetch<{ rule: InboundIqcRule }>("/api/shipping/inbound/iqc-rules", {
    method: "POST",
    body: input,
  });
  return data.rule;
}

export async function setInboundIqcRuleActive(ruleId: string, active: boolean): Promise<InboundIqcRule> {
  const data = await apiFetch<{ rule: InboundIqcRule }>(
    `/api/shipping/inbound/iqc-rules/${encodeURIComponent(ruleId)}`,
    { method: "PATCH", body: { active } },
  );
  return data.rule;
}

export interface HoldingIqcLine {
  id: string;
  sku: string;
  received_quantity: string;
  iqc_status: IqcStatus;
  iqc_notes: string | null;
  iqc_passed_quantity: string | null;
  iqc_failed_quantity: string | null;
  iqc_skipped_quantity: string | null;
  first_count_by: string | null;
  source_type: string;
  source_id: string;
  source_number: string | null;
}

export async function fetchHoldingIqcLines(scan?: string): Promise<HoldingIqcLine[]> {
  const data = await apiFetch<{ rows: HoldingIqcLine[] }>("/api/shipping/inbound/holding-iqc", {
    query: scan != null && scan.trim() !== "" ? { scan: scan.trim() } : undefined,
  });
  return data.rows;
}

export async function saveHoldingIqc(
  itemId: string,
  body: {
    iqc_status: IqcStatus;
    iqc_notes: string;
    iqc_passed_quantity: number | null;
    iqc_failed_quantity: number | null;
    iqc_skipped_quantity: number | null;
    pcb_full_panel_quantity?: number | null;
    pcb_partial_panel_quantity?: number | null;
    pcb_partial_panel_count?: number | null;
  },
): Promise<HoldingIqcLine> {
  const data = await apiFetch<{ item: HoldingIqcLine }>(
    `/api/shipping/inbound/receipt-items/${encodeURIComponent(itemId)}/iqc`,
    { method: "PATCH", body },
  );
  return data.item;
}

export async function printPcbPanelContainerLabels(
  itemId: string,
  printer: string,
): Promise<{ printed: number }> {
  return apiFetch<{ printed: number }>(
    `/api/shipping/inbound/receipt-items/${encodeURIComponent(itemId)}/pcb-panel-labels`,
    { method: "POST", body: { printer } },
  );
}

export interface ToStockLine {
  id: string;
  sku: string;
  received_quantity: string;
  source_type: string;
  source_id: string;
  source_number: string | null;
  put_away_at: string | null;
}

export interface ArrivalPackageLookup {
  arrival_id: string;
  package_no: number;
  vendor_name: string;
  po_number: string | null;
  carrier: string | null;
  tracking: string | null;
}

export type ToStockLookupResult =
  | { kind: "arrival_package"; package: ArrivalPackageLookup }
  | {
      kind: "to_stock";
      sku: string | null;
      rows: ToStockLine[];
      inventory_box_hex?: string | null;
      sku_sublocations?: string[];
      sku_sublocations_are_last?: boolean;
    }
  | { kind: "unknown"; scan: string };

export async function lookupToStock(scan?: string): Promise<ToStockLookupResult> {
  return apiFetch<ToStockLookupResult>("/api/shipping/inbound/to-stock", {
    query: scan != null && scan.trim() !== "" ? { scan: scan.trim() } : undefined,
  });
}

export async function confirmToStockPutAway(input: {
  inbound_receipt_item_id: string;
  sublocation: string;
  inventory_box_hex?: string | null;
}): Promise<ToStockLine> {
  const data = await apiFetch<{ item: ToStockLine }>("/api/shipping/inbound/to-stock/put-away", {
    method: "POST",
    body: {
      inbound_receipt_item_id: input.inbound_receipt_item_id,
      sublocation: input.sublocation.trim(),
      ...(input.inventory_box_hex != null && input.inventory_box_hex.trim() !== ""
        ? { inventory_box_hex: input.inventory_box_hex.trim() }
        : {}),
    },
  });
  return data.item;
}

/*
 * DEPRECATED (unified Holding): second-count and discrepancy event clients.
 * Routes commented out in shipping-inbound-routes.ts — use fetchHoldingTasks / inbound/iqc-rules.
 *
export interface SecondCountRule { ... }
export async function fetchSecondCountTasks(...) { ... }
export async function recordSecondCountRecount(...) { ... }
export async function fetchSecondCountRules(...) { ... }
export async function fetchSecondCountRuleOptions(...) { ... }
export async function createSecondCountRule(...) { ... }
export async function setSecondCountRuleActive(...) { ... }
export async function fetchDiscrepancyEvents(...) { ... }
 */

export interface InboundReceiveOrderOperator extends LaborOperatorDisplay {
  sourceType: InboundSourceType;
  sourceId: number;
  /** Dock crate this operator currently has open. Null when the session has not claimed a crate. */
  arrivalPackageId: number | null;
}

export const inboundReceiveOrderOperatorsQueryKey = [
  "shipping",
  "inbound",
  "receive-order-operators",
] as const;

export async function fetchInboundReceiveOrderOperators(): Promise<InboundReceiveOrderOperator[]> {
  const data = await apiFetch<{ operators: InboundReceiveOrderOperator[] }>(
    "/api/labor/receiving/active-receive-order-operators",
    { cache: "no-store" },
  );
  return data.operators;
}

export function formatInboundSourceType(sourceType: InboundSourceType): string {
  if (sourceType === "purchase_order") return "Purchase order";
  return "Transfer";
}

export function formatDate(raw: string | null): string {
  if (!raw) return "-";
  const parsed = new Date(raw.includes("T") ? raw : `${raw}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return raw;
  if (parsed.getFullYear() < 1951) return "-";
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** Formats comma-separated YYYY-MM-DD segments from the inbound list or detail (multiple delivery dates per order). */
export function formatInboundExpectedDates(raw: string | null): string {
  if (!raw?.trim()) return "-";
  const segments = raw
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const formatted = segments.map((segment) => formatDate(segment)).filter((value) => value !== "-");
  return formatted.length > 0 ? formatted.join(" · ") : "-";
}

export function formatNumber(value: string | number | null | undefined): string {
  const parsed = typeof value === "number" ? value : Number(value ?? 0);
  if (!Number.isFinite(parsed)) return "0";
  return parsed.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

/** Line ordered quantity minus Megaventory or transfer-recorded receipts, clamped at zero. */
export function remainingToReceiveQuantity(line: Pick<InboundLineRow, "expected_quantity" | "already_received_quantity">): number {
  const ordered = Number.parseFloat(line.expected_quantity);
  const booked = Number.parseFloat(line.already_received_quantity);
  const o = Number.isFinite(ordered) ? ordered : 0;
  const b = Number.isFinite(booked) ? booked : 0;
  return Math.max(0, o - b);
}
